/**
 * Hook for WebSocket-based report generation
 */

import { useState, useEffect, useRef } from 'react';
import { reportsApi, ReportRequest, ReportData, StartReportJobResponse } from '../services/reports.service';
import { webSocketService, Job, JobProgress } from '../services/websocket.service';

export interface ReportJobState {
  jobId: string | null;
  status: 'idle' | 'starting' | 'running' | 'completed' | 'failed';
  progress: JobProgress | null;
  result: ReportData | null;
  error: string | null;
}

export function useReportJob() {
  const [state, setState] = useState<ReportJobState>({
    jobId: null,
    status: 'idle',
    progress: null,
    result: null,
    error: null,
  });
  const [isConnected, setIsConnected] = useState(false);

  const currentJobIdRef = useRef<string | null>(null);

  // Connect to WebSocket on mount and track connection status
  useEffect(() => {
    webSocketService.connect();

    // Check connection status periodically
    const checkConnection = () => {
      setIsConnected(webSocketService.isConnected());
    };

    // Initial check
    checkConnection();

    // Check every 1 second
    const interval = setInterval(checkConnection, 1000);

    return () => {
      clearInterval(interval);
      if (currentJobIdRef.current) {
        webSocketService.unsubscribeFromJob(currentJobIdRef.current);
      }
    };
  }, []);

  const startReport = async (request: ReportRequest) => {
    try {
      // Clean up previous job
      if (currentJobIdRef.current) {
        webSocketService.unsubscribeFromJob(currentJobIdRef.current);
      }

      setState({
        jobId: null,
        status: 'starting',
        progress: null,
        result: null,
        error: null,
      });

      // Start the job
      const response: StartReportJobResponse = await reportsApi.startReportJob(request);
      const jobId = response.jobId;

      currentJobIdRef.current = jobId;

      setState(prev => ({
        ...prev,
        jobId,
        status: 'running',
      }));

      // Subscribe to WebSocket updates
      webSocketService.subscribeToJob(jobId, {
        onProgress: (job: Job) => {
          setState(prev => ({
            ...prev,
            status: 'running',
            progress: job.progress || null,
          }));
        },
        onCompleted: (job: Job) => {
          setState(prev => ({
            ...prev,
            status: 'completed',
            result: job.result,
          }));
          webSocketService.unsubscribeFromJob(jobId);
          currentJobIdRef.current = null;
        },
        onFailed: (job: Job) => {
          setState(prev => ({
            ...prev,
            status: 'failed',
            error: job.error || 'Unknown error',
          }));
          webSocketService.unsubscribeFromJob(jobId);
          currentJobIdRef.current = null;
        },
      });

    } catch (error) {
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to start report',
      }));
    }
  };

  const reset = () => {
    if (currentJobIdRef.current) {
      webSocketService.unsubscribeFromJob(currentJobIdRef.current);
      currentJobIdRef.current = null;
    }

    setState({
      jobId: null,
      status: 'idle',
      progress: null,
      result: null,
      error: null,
    });
  };

  return {
    ...state,
    startReport,
    reset,
    isConnected,
  };
}